import type {
  ClinicClosure,
  ClinicClosureResult,
  CreateClinicClosureInput,
  CreateDoctorExtraHoursInput,
  CreateDoctorTimeOffInput,
  DoctorExtraHours,
  DoctorTimeOff,
  DoctorTimeOffResult,
  ListClinicClosuresQuery,
  ListDoctorExtraHoursQuery,
  ListDoctorTimeOffQuery,
  Paginated,
  ScheduleConflictOptions,
  UpdateClinicClosureInput,
  UpdateDoctorTimeOffInput,
} from "@clinic/shared";
import { apiRequest } from "@web/shared/lib/api-client";

const options = (choice: Partial<ScheduleConflictOptions> | undefined) => ({
  ...(choice?.force && { force: "true" }),
  ...(choice?.cancelAppointments && { cancelAppointments: "true" }),
});

export const closuresApi = {
  list: (query: Partial<ListClinicClosuresQuery> = {}): Promise<Paginated<ClinicClosure>> =>
    apiRequest("/clinic-closures", {
      query: { page: query.page, limit: query.limit, from: query.from, to: query.to },
    }),

  create: (
    body: CreateClinicClosureInput,
    choice?: Partial<ScheduleConflictOptions>,
  ): Promise<ClinicClosureResult> =>
    apiRequest("/clinic-closures", { method: "POST", body, query: options(choice) }),

  update: (
    id: string,
    body: UpdateClinicClosureInput,
    choice?: Partial<ScheduleConflictOptions>,
  ): Promise<ClinicClosureResult> =>
    apiRequest(`/clinic-closures/${id}`, { method: "PATCH", body, query: options(choice) }),

  remove: (id: string): Promise<void> => apiRequest(`/clinic-closures/${id}`, { method: "DELETE" }),
};

export const timeOffApi = {
  list: (
    doctorId: string,
    query: Partial<ListDoctorTimeOffQuery> = {},
  ): Promise<Paginated<DoctorTimeOff>> =>
    apiRequest(`/doctors/${doctorId}/time-off`, {
      query: { page: query.page, limit: query.limit, from: query.from, to: query.to },
    }),

  create: (
    doctorId: string,
    body: CreateDoctorTimeOffInput,
    choice?: Partial<ScheduleConflictOptions>,
  ): Promise<DoctorTimeOffResult> =>
    apiRequest(`/doctors/${doctorId}/time-off`, { method: "POST", body, query: options(choice) }),

  update: (
    id: string,
    body: UpdateDoctorTimeOffInput,
    choice?: Partial<ScheduleConflictOptions>,
  ): Promise<DoctorTimeOffResult> =>
    apiRequest(`/doctor-time-off/${id}`, { method: "PATCH", body, query: options(choice) }),

  remove: (id: string): Promise<void> => apiRequest(`/doctor-time-off/${id}`, { method: "DELETE" }),
};

export const extraHoursApi = {
  list: (
    doctorId: string,
    query: Partial<ListDoctorExtraHoursQuery> = {},
  ): Promise<Paginated<DoctorExtraHours>> =>
    apiRequest(`/doctors/${doctorId}/extra-hours`, {
      query: { page: query.page, limit: query.limit, from: query.from, to: query.to },
    }),

  create: (doctorId: string, body: CreateDoctorExtraHoursInput): Promise<DoctorExtraHours> =>
    apiRequest(`/doctors/${doctorId}/extra-hours`, { method: "POST", body }),

  remove: (id: string): Promise<void> =>
    apiRequest(`/doctor-extra-hours/${id}`, { method: "DELETE" }),
};
